/**
 * Deduction Report — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/deduction-report.routes.ts`
 * and `apps/api/src/routes/tenant/deduction-report.routes.ts` import these to register their
 * routes, and `apps/landlord/src/lib/condition.api.ts` /
 * `apps/tenant/src/lib/condition.api.ts` derive their response/body types from the same
 * objects. See packages/api-types/README.md.
 *
 * The response shapes (`DeductionLineItemResponseSchema`, `DeductionReportResponseSchema`)
 * are identical between the manager and tenant routes. The manager route additionally owns
 * the write path (compile/edit/generate/send/retry-delivery) — those schemas have no tenant
 * equivalent (BR-17: draft amounts are landlord-only; the tenant route is read-only, gated
 * to `status = SENT`), so they stay in this one file alongside the shared read shapes rather
 * than forking a separate manager-only file (same one-file-per-domain precedent as
 * `leasing/listings.ts` mixing CRUD + lifecycle schemas for a single audience).
 *
 * Covers:
 *   GET  /landlord/api/leases/:leaseId/deduction-report
 *   POST /landlord/api/leases/:leaseId/deduction-report/compile
 *   PATCH /landlord/api/deduction-report/line-items/:lineItemId
 *   POST /landlord/api/deduction-reports/:reportId/generate
 *   POST /landlord/api/deduction-reports/:reportId/send
 *   POST /landlord/api/deduction-reports/:reportId/retry-delivery
 *   GET  /landlord/api/deduction-reports/:reportId
 *   GET  /tenant/api/deduction-reports/:reportId  (BR-17: only once status=SENT)
 */
import { z } from 'zod';
// `ItemConditionSchema`/`DeficiencyTypeSchema` are identical to (and re-exported alongside)
// the walkthrough domain's copies — see `condition/template.ts`'s identical note on
// `ItemCategorySchema` for why this file imports the single source of truth instead of
// re-declaring an `export *`-colliding duplicate.
import { ItemConditionSchema, DeficiencyTypeSchema, } from './walkthrough.js';
export { ItemConditionSchema, DeficiencyTypeSchema };
// ============================================================================
// ENUMS
// ============================================================================
export const BaselineStatusSchema = z.enum(['MATCHED', 'UNVERIFIED']);
export const ReportStatusSchema = z.enum(['DRAFT', 'GENERATED', 'SENT']);
export const DeliveryStatusSchema = z.enum(['PENDING', 'SENT', 'FAILED']);
// ============================================================================
// REQUEST — manager-only (write path)
// ============================================================================
export const EditLineItemBodySchema = z
    .object({
    amountCents: z.number().int().min(0).optional(),
    description: z.string().min(1).max(2000).optional(),
    receiptStorageKey: z.string().max(1024).nullable().optional(),
    displayOrder: z.number().int().min(0).optional(),
})
    .refine((data) => Object.keys(data).length > 0, { message: 'At least one field must be provided for update' });
// ============================================================================
// RESPONSE — shared between manager and tenant
// ============================================================================
export const DeductionLineItemResponseSchema = z.object({
    id: z.uuid(),
    moveOutItemId: z.uuid(),
    moveInItemId: z.uuid().nullable(),
    baselineStatus: BaselineStatusSchema,
    description: z.string(),
    amountCents: z.number().int(),
    receiptStorageKey: z.string().nullable(),
    displayOrder: z.number(),
    room: z.string(),
    item: z.string(),
    moveIn: z.object({ condition: ItemConditionSchema.nullable(), photoCount: z.number().int().min(0) }),
    moveOut: z.object({
        condition: ItemConditionSchema,
        deficiencyTypes: z.array(DeficiencyTypeSchema),
        narrative: z.string().nullable(),
        photoCount: z.number().int().min(0),
    }),
    tenantMoveOut: z.object({ condition: ItemConditionSchema, photoCount: z.number().int().min(0) }).nullable(),
});
export const DeductionReportResponseSchema = z.object({
    id: z.uuid(),
    leaseId: z.uuid(),
    status: ReportStatusSchema,
    totalCents: z.number().int(),
    generatedAt: z.string().nullable(),
    sentAt: z.string().nullable(),
    deliveryStatus: DeliveryStatusSchema,
    deliveryEmail: z.string().nullable(),
    lineItems: z.array(DeductionLineItemResponseSchema),
    acknowledgment: z.object({
        landlordMoveInAckedByTenant: z.boolean(),
        landlordMoveOutAckedByTenant: z.boolean(),
    }),
});
export const ReportWrapperResponseSchema = z.object({ report: DeductionReportResponseSchema });
/** Manager-only: `GET .../deduction-report` never compiles, so the report may not exist yet. */
export const NullableReportWrapperResponseSchema = z.object({ report: DeductionReportResponseSchema.nullable() });
/** Manager-only: `POST .../generate` response, carrying non-fatal render warnings. */
export const GenerateResponseSchema = z.object({ report: DeductionReportResponseSchema, warnings: z.array(z.string()) });
/**
 * One reason the report isn't ready to generate — the `gaps` array entry of the manager-only
 * 422 `ReportNotReadyResponseSchema` (that wrapper schema itself stays route-local: it extends
 * apps/api's `ErrorResponseSchema`, which this zod-only package cannot import — same
 * precedent as `condition/walkthrough.ts`'s `WalkthroughIncompleteGapSchema`).
 */
export const ReportNotReadyGapSchema = z.object({
    code: z.enum(['MOVE_OUT_NOT_SUBMITTED', 'MOVE_IN_NOT_SUBMITTED', 'LINE_NOT_PRICED']),
    message: z.string(),
    lineItemId: z.uuid().optional(),
});
//# sourceMappingURL=deduction-report.js.map